import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { equals } from 'ramda';
import { noop } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import CircleInfo from '@gef-ui/material-icons/CircleInfo';

import TaskFlag from 'components/atoms/SvgrIcons/TaskFlag';
import taskSearch from 'features/views/taskSearch';
import { equalOrThreeDashes, fromIsoDate, getCodelistPropById, getRoutePath } from 'utils';
import { getClientName } from 'utils/client';

const ResultsTableRow = ({ task, finishTypesAllCodelist, taskStatusCodelist, taskTypesCodelist }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const selectedTasks = useSelector(taskSearch.selectors.getSelectedTaskIds);
	const isSelected = useMemo(() => Boolean(selectedTasks.find(equals(task.id))), [selectedTasks, task.id]);

	const handleToggleTask = useCallback(() => {
		if (isSelected) {
			dispatch(taskSearch.actions.unselectTask(task.id));
		} else {
			dispatch(taskSearch.actions.selectTask(task.id));
		}
	}, [dispatch, isSelected, task.id]);

	const handleRecordClick = useCallback(
		(e) => {
			e.stopPropagation();

			const { pathname, state } = getRoutePath('detail', { taskId: task?.id }, true);
			navigate(pathname, { state });
		},
		[task?.id, navigate],
	);

	const { policy = {}, client = {} } = task;
	const { address = {} } = client;

	const infoOrTaskIcon = useMemo(() => {
		if (task?.informative) {
			return <CircleInfo sx={{ color: 'info.light' }} />;
		} else {
			return <TaskFlag sx={{ color: 'primary.light', fontSize: 17, ml: 1 }} />;
		}
	}, [task?.informative]);

	return (
		<TableRow
			onClick={task.id ? handleToggleTask : noop}
			hover={!isSelected}
			sx={{
				backgroundColor: isSelected ? 'primary.shades.8p' : 'default',
			}}
		>
			<TableCell>{infoOrTaskIcon}</TableCell>
			<TableCell>
				{!task.id && !task.statusId ? (
					equalOrThreeDashes()
				) : (
					<>
						<div>{task.id}</div>
						<div>{getCodelistPropById(task.statusId, taskStatusCodelist)}</div>
					</>
				)}
			</TableCell>
			<TableCell>
				<div>{equalOrThreeDashes(getClientName(client))}</div>
				<div>{equalOrThreeDashes(address?.municipality)}</div>
			</TableCell>
			<TableCell>{equalOrThreeDashes(policy?.policyNo)}</TableCell>
			<TableCell>
				<div>{getCodelistPropById(task.taskTypeId, taskTypesCodelist)}</div>
				<div>{equalOrThreeDashes(getCodelistPropById(task.finishTypeId, finishTypesAllCodelist))}</div>
			</TableCell>
			<TableCell>{task.queueGroup}</TableCell>
			<TableCell>
				<div>{fromIsoDate(task.dateCreated)}</div>
				<div>{fromIsoDate(task.deadlineDate)}</div>
			</TableCell>
			<TableCell>
				<Link
					underline="none"
					onClick={handleRecordClick}
					sx={{
						fontWeight: 'bold',
					}}
				>
					{getLabelMessage('common:detail')}
				</Link>
			</TableCell>
		</TableRow>
	);
};

ResultsTableRow.propTypes = {
	finishTypesAllCodelist: PropTypes.array,
	task: PropTypes.object,
	taskStatusCodelist: PropTypes.array,
	taskTypesCodelist: PropTypes.array,
};

export default ResultsTableRow;
